/* eslint-disable react-hooks/exhaustive-deps */

import type { FC, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';

import { Box, Typography } from '@mui/material';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import { CrossIcon } from '@setup/svg-icons/svg-icons';
import theme from '@setup/theme/theme';
import { MuiFloatButton, MuiTooltip } from '@shared/ui-kit';

import {
  CreateActionMenuContainer,
  CreateActionMenuHeader,
  CreateActionMenuItem,
} from './CreateActionMenu.style';

import type { SxProps, Theme } from '@mui/material';
import type { ActionMenuListModel } from '@shared/models/actionMenuList.model';
import type { PopupState } from 'material-ui-popup-state/hooks';

export type CreateActionMenuProps = {
  menuList: ActionMenuListModel;
  header: string | null;
  isMobile: boolean;
  anchorOriginVertical?: 'bottom' | 'top' | 'center';
  anchorOriginHorizontal?: 'center' | 'right' | 'left';
  transformOriginVertical?: 'bottom' | 'top' | 'center';
  transformOriginHorizontal?: 'center' | 'right' | 'left';
  childrenComponent: ReactNode | null;
  sx?: SxProps<Theme> | undefined;
  isMobileVariant?: boolean;
  setIsOpen?: (val: boolean) => void;
  isCustomPopupState?: boolean;
  isOpen?: boolean;
};

export const CreateActionMenu: FC<CreateActionMenuProps> = ({
  menuList,
  header,
  childrenComponent,
  isMobile,
  anchorOriginHorizontal = 'center',
  anchorOriginVertical = 'top',
  transformOriginHorizontal = 'center',
  transformOriginVertical = 'bottom',
  sx,
  isMobileVariant = true,
  setIsOpen,
  isOpen,
  isCustomPopupState,
}) => {
  const anchorRef = useRef<HTMLElement | null>(null);

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'demo-popup-menu',
  });

  useEffect(() => {
    if (isCustomPopupState && anchorRef.current) {
      popupState.setAnchorEl(anchorRef.current);
    }
  }, [isCustomPopupState]);

  const handleClickAwayPopup = (close: () => void, event?: React.MouseEvent) => {
    close();
    if (setIsOpen && isCustomPopupState) {
      setIsOpen(false);
    }
    if (event) {
      event.stopPropagation();
    }
  };

  const handleOpenPopup = (open: (e: React.MouseEvent) => void, event: React.MouseEvent) => {
    if (setIsOpen && isCustomPopupState) {
      setIsOpen(true);
    }
    open(event);
  };
  return (
    <Box>
      <Box onClick={(e) => e.stopPropagation()}>
        {childrenComponent !== null ? (
          <Box
            sx={{ display: 'inline-block', cursor: 'pointer' }}
            ref={anchorRef}
            {...bindTrigger({
              ...popupState,
              open: (e: React.MouseEvent) => handleOpenPopup(popupState.open, e),
            } as PopupState)}
          >
            {childrenComponent}
          </Box>
        ) : (
          <Box
            ref={anchorRef}
            {...bindTrigger({
              ...popupState,
              open: (e: React.MouseEvent) => handleOpenPopup(popupState.open, e),
            } as PopupState)}
          >
            <MuiFloatButton size="medium" {...bindTrigger(popupState)} />
          </Box>
        )}

        <CreateActionMenuContainer
          isMobileVariant={isMobileVariant}
          isMobile={isMobile}
          sx={{ ...sx }}
          {...bindMenu({
            ...popupState,
            close: (e: React.MouseEvent) => handleClickAwayPopup(popupState.close, e),
            isOpen: isCustomPopupState ? isOpen : popupState.isOpen,
          } as PopupState)}
          anchorOrigin={{
            vertical: anchorOriginVertical,
            horizontal: anchorOriginHorizontal,
          }}
          transformOrigin={{
            vertical: transformOriginVertical,
            horizontal: transformOriginHorizontal,
          }}
        >
          {header !== null && (
            <CreateActionMenuHeader>
              <Typography sx={{ color: theme.palette.case.neutral.n700 }} variant="h3">
                {header}
              </Typography>
              <Box
                onClick={(e) => handleClickAwayPopup(popupState.close, e)}
                sx={{
                  padding: '4px',
                  cursor: 'pointer',
                  svg: {
                    width: '18px',
                    height: '18px',
                  },
                }}
              >
                <CrossIcon />
              </Box>
            </CreateActionMenuHeader>
          )}
          <Box
            display="flex"
            flexDirection="column"
            gap={isMobileVariant ? '8px' : '0'}
            padding={isMobile ? '16px' : '4px'}
            alignItems="center"
          >
            {menuList?.map((item, index) => (
              <React.Fragment key={index}>
                <MuiTooltip title={item?.tooltipTitle || ''} tooltipWithIcon={false}>
                  <Box width="100%">
                    <CreateActionMenuItem
                      id={item.itemId}
                      isMobileVariant={isMobileVariant}
                      isBorderBottom={item?.isBorderBottom}
                      isMobile={isMobile}
                      disabled={item.isDisabled}
                      onClick={() => {
                        handleClickAwayPopup(popupState.close);
                        item.callback();
                      }}
                    >
                      <Box display="flex" alignItems="center" gap="8px">
                        {item.startIcon && <item.startIcon />}

                        {item.customLabel ? (
                          item?.customLabel
                        ) : (
                          <Typography
                            variant={!isMobileVariant ? 't14r' : 's2'}
                            sx={{ color: theme.palette.case.neutral.n600 }}
                          >
                            {item?.label}
                          </Typography>
                        )}
                      </Box>
                    </CreateActionMenuItem>
                  </Box>
                </MuiTooltip>
              </React.Fragment>
            ))}
          </Box>
        </CreateActionMenuContainer>
      </Box>
    </Box>
  );
};
